import React, { useEffect, useState } from "react";
import api from "../api/config";
import {
  GetAllUsers,
  GetUserAuditHistory,
  LockUser,
  UnlockUser,
} from "../api/Users";
import TableWithPagination from "../components/shared/TableWithPagination";
import { Box, Button } from "@mui/material";
import UserAuditHistory from "../components/shared/UserAuditHistory";
import { useNotification } from "../context/NotificationContext";
import EditUserModal from "../components/shared/EditUserModal";
import { GetAllClients } from "../api/Patient";
import { GetAllAccessLevels } from "../api/Access";
import AddUserAccessModal from "../components/shared/AddUserAccessModal";
import NewUserModal from "../components/shared/NewUserModal";
import AccessLevel from "../components/shared/AccessLevel";

const UserManagement = () => {
  const [tableData, setTableData] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersAuditLoading, setUsersAuditLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [userAuditPage, setUserAuditPage] = useState(0);
  const [userAuditLimit, setUserAuditLimit] = useState(10);
  const [totalAuditRecords, setTotalAuditRecords] = useState(0);
  const [openAuditHistoryModal, setOpenAuditHistoryModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState({
    first_name: "",
    last_name: "",
    email: "",
  });
  const [auditHistoryForUser, setAuditHistoryForUser] = useState([]);
  const { showNotification } = useNotification();
  const [openEditUserModal, setOpenEditUserModal] = useState(false);
  const [accessLevels, setAccessLevels] = useState([]);
  const [clients, setClients] = useState([]);
  const [openAddUserAccessModal, setOpenAddUserAccessModal] = useState(false);
  const [openNewUserModal, setOpenNewUserModal] = useState(false);
  const [openAddAccessLevelModal, setOpenAddAccessLevelModal] = useState(false);

  const columns = [
    { id: "first_name", label: "First Name" },
    { id: "last_name", label: "Last Name" },
    { id: "email", label: "Email" },
    { id: "phone_number", label: "Phone" },
    { id: "access_level", label: "Access Level" },
    { id: "account_locked", label: "Locked?", type: "boolean" },
    { id: "failed_attempts", label: "Failed Attempts" },
    { id: "organization", label: "Organization" },
    {
      id: "actions",
      label: "Actions",
      buttons: [
        { label: "User Audit History", id: "audit" },
        { label: "Edit User", id: "edit" },
        { label: "Add User Access", id: "access" },
        { label: "Lock User", id: "lock" },
        { label: "Unlock User", id: "unlock" },
      ],
    },
  ];
  const [accessLevelsLoading, setAccessLevelsLoading] = useState(false);

  const getAllUsers = async () => {
    setUsersLoading(true);
    try {
      const clientUuid = localStorage.getItem("clientuuid");
      const offset = page * limit;
      const response = await api.get(GetAllUsers, {
        params: {
          clientUID: clientUuid,
          offset,
          limit,
        },
      });
      const { total_count, users } = response.data;
      setTableData(users);
      setTotalRecords(total_count);
    } catch (error) {
      console.error("Error fetching users", error);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    getAllUsers();
  }, [page, limit]);

  useEffect(() => {
    if (openAuditHistoryModal) {
      getUserAuditHistory(selectedUser);
    }
  }, [userAuditPage, userAuditLimit]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(0);
  };

  const handleAuditPageChange = (newPage) => {
    setUserAuditPage(newPage);
  };

  const handleAuditLimitChange = (newLimit) => {
    setUserAuditLimit(newLimit);
    setUserAuditPage(0);
  };

  const handleActionClick = (actionType, row) => {
    console.log("Action clicked:", row);
    setSelectedUser(row);
    if (actionType === "audit") {
      getUserAuditHistory(row);
      setOpenAuditHistoryModal(true);
    } else if (actionType === "edit") {
      setOpenEditUserModal(true);
    } else if (actionType === "access") {
      setOpenAddUserAccessModal(true);
    } else if (actionType === "lock") {
      lockUser(row);
    } else if (actionType === "unlock") {
      unlockUser(row);
    }
  };

  const lockUser = async (user) => {
    try {
      const response = await api.post(`${LockUser}/${user.email}`);
      console.log("Lock user response:", response);
      showNotification("User locked successfully", "success");
      getAllUsers();
    } catch (error) {
      console.error("Error locking user", error);
      showNotification("Failed to lock user", "error");
    }
  };

  const unlockUser = async (user) => {
    try {
      const response = await api.post(`${UnlockUser}/${user.email}`);
      console.log("Unlock user response:", response);
      showNotification("User unlocked successfully", "success");
      getAllUsers();
    } catch (error) {
      console.error("Error locking user", error);
      showNotification("Failed to unlock user", "error");
    }
  };

  const getUserAuditHistory = async (user) => {
    setUsersAuditLoading(true);
    try {
      const clientUuid = localStorage.getItem("clientuuid");
      const offset = userAuditPage * userAuditLimit;
      const response = await api.get(`${GetUserAuditHistory}/${user.email}`, {
        params: {
          clientUID: clientUuid,
          offset,
          limit: userAuditLimit,
        },
      });
      const { total_logs, audit } = response.data;
      setAuditHistoryForUser(audit);
      setTotalAuditRecords(total_logs);
    } catch (error) {
      console.error("Error fetching users", error);
    } finally {
      setUsersAuditLoading(false);
    }
  };

  const getAllClients = async () => {
    try {
      const response = await api.get(GetAllClients);
      setClients(response.data.clients);
      console.log("Clients:", response.data.clients);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };
  const getAllAccessLevels = async () => {
    setAccessLevelsLoading(true);
    try {
      const clientUuid = localStorage.getItem("clientuuid");
      const response = await api.get(`${GetAllAccessLevels}/${clientUuid}`);
      setAccessLevels(response.data);
      console.log("Access Levels:", response.data);
    } catch (error) {
      console.error("Error fetching Access Levels:", error);
    } finally {
      setAccessLevelsLoading(false);
    }
  };

  const handleOpenNewUserModal = () => {
    setOpenNewUserModal(true);
  };

  const handleAddAccessLevelModal = () => {
    setOpenAddAccessLevelModal(true);
  };

  useEffect(() => {
    getAllClients();
    getAllAccessLevels();
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100vw",
        overflowX: "hidden",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenNewUserModal}
        >
          Add User
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddAccessLevelModal}
        >
          Access Levels
        </Button>
      </Box>
      <Box sx={{ width: "100%" }}>
        <TableWithPagination
          title="User Management"
          columns={columns}
          rows={tableData}
          isLoading={usersLoading}
          onRefresh={getAllUsers}
          totalRecords={totalRecords}
          page={page}
          limit={limit}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          onActionClick={handleActionClick}
          noSearch={true}
        />
      </Box>
      <UserAuditHistory
        open={openAuditHistoryModal}
        onClose={() => setOpenAuditHistoryModal(false)}
        user={selectedUser}
        auditHistory={auditHistoryForUser}
        totalRecords={totalAuditRecords}
        page={userAuditPage}
        limit={userAuditLimit}
        onPageChange={handleAuditPageChange}
        onLimitChange={handleAuditLimitChange}
        isLoading={usersAuditLoading}
      />
      <EditUserModal
        open={openEditUserModal}
        onClose={() => setOpenEditUserModal(false)}
        user={selectedUser}
        accessLevels={accessLevels}
        clients={clients}
        onSaveSuccess={() => {
          getAllUsers();
          setOpenEditUserModal(false);
          showNotification("User updated successfully", "success");
        }}
      />
      <AddUserAccessModal
        open={openAddUserAccessModal}
        onClose={() => setOpenAddUserAccessModal(false)}
        user={selectedUser}
        accesses={accessLevels}
        onSaveSuccess={() => {
          getAllUsers();
          setOpenAddUserAccessModal(false);
          showNotification("User access level updated successfully", "success");
        }}
      />
      <NewUserModal
        open={openNewUserModal}
        onClose={() => setOpenNewUserModal(false)}
        clients={clients}
        accessLevels={accessLevels}
        onSaveSuccess={() => {
          getAllUsers();
          setOpenNewUserModal(false);
          showNotification("User added successfully", "success");
        }}
      />
      <AccessLevel
        open={openAddAccessLevelModal}
        onClose={() => setOpenAddAccessLevelModal(false)}
        clients={clients}
        accessLevels={accessLevels}
        onSaveSuccess={() => {
          getAllUsers();
          setOpenAddAccessLevelModal(false);
          showNotification("Access Level added successfully", "success");
        }}
        refreshAccessLevels={getAllAccessLevels}
        accessLevelsLoading={accessLevelsLoading}
      />
    </Box>
  );
};

export default UserManagement;
